'use client';

import { createClient } from '@/lib/supabase/client';
import type { OutfitData, WizardData } from '@/app/fashion-discovery-interface/lib/types';

export interface SavedOutfit {
  id: string;
  userId: string;
  outfit: OutfitData;
  wizardData: WizardData;
  createdAt: string;
}

function rowToSavedOutfit(row: any): SavedOutfit {
  return {
    id: row.id,
    userId: row.user_id,
    outfit: {
      editorialTitle: row.editorial_title,
      editorialSubtitle: row.editorial_subtitle ?? '',
      aesthetic: row.aesthetic ?? '',
      stylistNotes: row.stylist_notes ?? '',
      keyWords: row.key_words ?? [],
      items: row.items ?? [],
    },
    wizardData: row.wizard_data,
    createdAt: row.created_at,
  };
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error/i.test(
      error.message
    );
  }
  return false;
}

export const savedOutfitsService = {
  async getAll(): Promise<SavedOutfit[]> {
    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return [];

    try {
      const { data, error } = await supabase
        .from('saved_outfits')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data?.map(rowToSavedOutfit) ?? [];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async save(outfit: OutfitData, wizardData: WizardData): Promise<SavedOutfit | null> {
    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('saved_outfits')
        .insert({
          user_id: user.id,
          editorial_title: outfit.editorialTitle,
          editorial_subtitle: outfit.editorialSubtitle,
          aesthetic: outfit.aesthetic,
          stylist_notes: outfit.stylistNotes,
          key_words: outfit.keyWords,
          items: outfit.items,
          wizard_data: wizardData,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? rowToSavedOutfit(data) : null;
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return null;
    }
  },

  async remove(id: string): Promise<boolean> {
    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('saved_outfits')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return false;
    }
  },

  async checkIsSaved(outfit: OutfitData): Promise<string | null> {
    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('saved_outfits')
        .select('id')
        .eq('user_id', user.id)
        .eq('editorial_title', outfit.editorialTitle)
        .maybeSingle();

      if (error) return null;
      return data?.id ?? null;
    } catch {
      return null;
    }
  },
};
